export interface DirtyStateSerializationOptions<T> {
  normalize?: (payload: T) => unknown;
  ignorePaths?: string[];
  trimStrings?: boolean;
}

interface CanonicalizeOptions {
  ignorePaths?: Set<string>;
  trimStrings: boolean;
}

function shouldIgnorePath(path: string[], ignorePaths?: Set<string>): boolean {
  if (!ignorePaths || ignorePaths.size === 0 || path.length === 0) return false;

  const fullPath = path.join('.');
  const leafKey = path[path.length - 1] ?? '';
  return ignorePaths.has(fullPath) || ignorePaths.has(leafKey);
}

function canonicalizeValue(value: unknown, path: string[], options: CanonicalizeOptions): unknown {
  if (shouldIgnorePath(path, options.ignorePaths)) {
    return undefined;
  }

  if (value === null || value === undefined) {
    return null;
  }

  if (typeof value === 'string') {
    const normalized = value.replace(/\r\n?/g, '\n');
    return options.trimStrings ? normalized.trim() : normalized;
  }

  if (typeof value === 'number' || typeof value === 'boolean') {
    return value;
  }

  if (value instanceof Date) {
    return value.toISOString();
  }

  if (Array.isArray(value)) {
    return value
      .map((entry, index) => canonicalizeValue(entry, [...path, String(index)], options))
      .filter((entry) => entry !== undefined);
  }

  if (typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>)
      .sort(([left], [right]) => left.localeCompare(right))
      .flatMap(([key, entryValue]) => {
        const normalized = canonicalizeValue(entryValue, [...path, key], options);
        return normalized === undefined ? [] : [[key, normalized] as const];
      });

    return Object.fromEntries(entries);
  }

  return String(value);
}

export function createDirtyStateSnapshot<T>(
  payload: T,
  options: DirtyStateSerializationOptions<T> = {}
): string {
  const normalized = options.normalize ? options.normalize(payload) : payload;
  const canonical = canonicalizeValue(normalized, [], {
    ignorePaths: options.ignorePaths ? new Set(options.ignorePaths) : undefined,
    trimStrings: options.trimStrings ?? false,
  });
  return JSON.stringify(canonical);
}
